import { cloneElement, Fragment, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import { useI18n } from '../../i18n';
import Notice from './Notice';

/**
 * #665 (F66) — one field of a form: the label, the control, the help line and
 * the error. Before, each form wrote `<div><label className="label">` by hand.
 * 102 of 110 labels named no control. A tap on the label did not focus the field.
 * A screen reader read an unnamed field. 30 required marks were typed into the
 * label text. At 10 fields the mark and the `required` attribute did not agree.
 *
 *   <Field label={t.profile.firstName} required>
 *     <input className="input" value={v} onChange={…} data-testid="…" />
 *   </Field>
 *
 * The DOM stays `div > label.label + control`, so the look does not change.
 *
 * The tie. Field gives the label `htmlFor`, and the control gets the same id:
 *   - The single child is the control. Field clones it with the id. A child
 *     that has its own `id` keeps it, and the label uses that id.
 *   - A control that is not the direct child (a wrapper, a branch) needs
 *     `htmlFor`. Then Field does not clone: put `id={htmlFor}` on the control.
 *     Also put `aria-describedby` and `aria-invalid` on it yourself. The help
 *     id is `<htmlFor>-help`, and the error id is `<htmlFor>-error`.
 *   - A component child (CountryPicker, CoachRoleSelect) must pass `id`,
 *     `required`, `aria-describedby` and `aria-invalid` to its control.
 *   - In development, Field warns (console.warn) when the label ties to
 *     nothing: no `group`, no `htmlFor`, and the child is not one element.
 *
 * The props:
 *   - `required` draws the mark " *" and sets `required` on the control.
 *     Do not type a mark into the label. A control that has `required` and
 *     no mark (the sign-in form) keeps its own `required` attribute.
 *   - `optional` draws the note "(optional)" after the label.
 *   - `hint` draws a grey note inside the label, after the mark.
 *   - `help` draws a grey help line under the control, and the control names
 *     it in `aria-describedby`.
 *   - `error` draws a `Notice` (tone error, role alert) under the control.
 *     The control gets `aria-invalid` and names the line in
 *     `aria-describedby`. An empty error ('', null, false) draws nothing.
 *     `errorTestId` is the `data-testid` of the line.
 *   - `group` is for a caption over several controls (a list of check
 *     boxes) or over a read-only value (the side judges of a bout). The
 *     caption is no `<label>`: the wrapper gets `role="group"` and
 *     `aria-labelledby`. Field does not clone the children.
 *   - `labelClassName` replaces the class `label` of the caption (a small
 *     caption in a dense row). The default is `label`.
 *   - `className` goes to the wrapper (`col-span-2`, `sm:col-span-3`).
 */
interface FieldProps {
  label: ReactNode;
  required?: boolean;
  optional?: boolean;
  hint?: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  errorTestId?: string;
  htmlFor?: string;
  group?: boolean;
  className?: string;
  labelClassName?: string;
  children: ReactNode;
}

/** The props that Field gives to its control. */
interface ControlProps {
  id?: string;
  required?: boolean;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
}

/** True when the node draws something: '', null, undefined and false draw nothing. */
const shows = (node: ReactNode) => node !== undefined && node !== null && node !== false && node !== '';

export default function Field({
  label, required = false, optional = false, hint, help, error, errorTestId,
  htmlFor, group = false, className, labelClassName = 'label', children,
}: FieldProps) {
  const { t } = useI18n();
  const autoId = useId();
  const control: ReactElement<ControlProps> | null =
    !group && !htmlFor && isValidElement<ControlProps>(children) && children.type !== Fragment ? children : null;
  if (import.meta.env.DEV && !group && !htmlFor && !control) {
    console.warn('Field: the label ties to nothing. Give Field one element as its child, or htmlFor, or group.');
  }
  const id = htmlFor ?? control?.props.id ?? autoId;
  const helpId = shows(help) ? `${id}-help` : undefined;
  const errorId = shows(error) ? `${id}-error` : undefined;

  const caption = (
    <>
      {label}
      {/* A control with `required` tells a screen reader itself, so the mark is for the eye only. */}
      {required && <span aria-hidden="true"> *</span>}
      {/* A group has no control with `required`, so a screen reader needs the word. */}
      {required && group && <span className="sr-only">{t.common.required}</span>}
      {optional && <span className="text-gray-400 font-normal text-xs"> {t.common.optional}</span>}
      {shows(hint) && <span className="text-gray-400 font-normal ml-1 text-xs">{hint}</span>}
    </>
  );

  let body: ReactNode = children;
  if (control) {
    const extra: ControlProps = { id };
    // Add a key only when it is set: an undefined value would remove the prop of the child.
    if (required) extra.required = true;
    const describedBy = [control.props['aria-describedby'], helpId, errorId].filter(Boolean).join(' ');
    if (describedBy) extra['aria-describedby'] = describedBy;
    if (errorId) extra['aria-invalid'] = true;
    body = cloneElement(control, extra);
  }

  const below = (
    <>
      {helpId && <p id={helpId} className="text-xs text-gray-400 mt-1">{help}</p>}
      {errorId && (
        <Notice tone="error" plain size="xs" className="mt-1" id={errorId} data-testid={errorTestId}>{error}</Notice>
      )}
    </>
  );

  if (group) {
    const captionId = `${id}-caption`;
    const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;
    return (
      <div className={className} role="group" aria-labelledby={captionId} aria-describedby={describedBy}>
        <div id={captionId} className={labelClassName}>{caption}</div>
        {body}
        {below}
      </div>
    );
  }

  return (
    <div className={className}>
      <label className={labelClassName} htmlFor={id}>{caption}</label>
      {body}
      {below}
    </div>
  );
}
